import {
  Tier1ImplementationReport,
  Tier1Plan,
  Tier1SleeveRow,
} from '../../types/tier1Portfolio';

/** Policy weights from pending plan; actuals from current implementation (Schwab/registry). */
export function buildPendingPlanPreviewSleeves(
  pending: Tier1Plan,
  implementation: Tier1ImplementationReport | null,
  tierBudgetUsd: number,
): Tier1SleeveRow[] {
  const budget = Math.max(0, tierBudgetUsd);
  const implByKey = new Map(
    (implementation?.sleeves ?? []).map((s) => [s.sleeveKey, s]),
  );
  const holdingBySleeve = new Map(
    (pending.holdings ?? []).map((h) => [h.sleeveKey, h.symbol]),
  );

  return (pending.sleeveTargets ?? []).map((t) => {
    const existing = implByKey.get(t.sleeveKey);
    const actualNotionalUsd = existing?.actualNotionalUsd ?? 0;
    const policyNotionalUsd = budget * (t.targetWeightPct / 100);
    const actualWeightPct = budget > 0 ? (actualNotionalUsd / budget) * 100 : 0;
    const deltaWeightPct = actualWeightPct - t.targetWeightPct;
    const needsAdditionalUsd =
      Math.round(Math.max(0, policyNotionalUsd - actualNotionalUsd) * 100) / 100;

    return {
      sleeveKey: t.sleeveKey,
      name: t.name ?? existing?.name,
      policyWeightPct: t.targetWeightPct,
      /** Frozen weights on the current active plan (when superseding). */
      currentPolicyWeightPct: existing?.policyWeightPct,
      actualWeightPct: Math.round(actualWeightPct * 100) / 100,
      deltaWeightPct: Math.round(deltaWeightPct * 100) / 100,
      policyNotionalUsd: Math.round(policyNotionalUsd * 100) / 100,
      actualNotionalUsd: Math.round(actualNotionalUsd * 100) / 100,
      holdings: existing?.holdings ?? [],
      intendedSymbol: holdingBySleeve.get(t.sleeveKey) ?? existing?.intendedSymbol,
      positionId: existing?.positionId,
      fillStatus: existing?.fillStatus ?? 'empty',
      needsAdditionalUsd,
      tagVerified: existing?.tagVerified,
      registryStatus: existing?.registryStatus,
      plannedAction: undefined,
    };
  });
}

export function sleeveTrackingErrorPct(sleeves: Tier1SleeveRow[]): number {
  let sumSq = 0;
  for (const row of sleeves) {
    sumSq += row.deltaWeightPct * row.deltaWeightPct;
  }
  return Math.round(Math.sqrt(sumSq) * 100) / 100;
}
